import React, { useState } from 'react';

/**
 * Computes a readable text color (black/white) based on background brightness.
 * @param {string} color - Hex or rgb(a) color string.
 * @returns {string} '#000' or '#fff'
 */
function getContrastingTextColor(color) {
    if (!color || typeof color !== 'string') return '#000';

    // Handle hex colors: #RGB or #RRGGBB
    const hexMatch = color.replace('#', '').match(/^([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/);
    if (hexMatch) {
        let hex = hexMatch[0];
        if (hex.length === 3) {
            hex = hex.split('').map((c) => c + c).join('');
        }
        const intVal = parseInt(hex, 16);
        const r = (intVal >> 16) & 255;
        const g = (intVal >> 8) & 255;
        const b = intVal & 255;
        const brightness = (r * 299 + g * 587 + b * 114) / 1000;
        return brightness > 140 ? '#000' : '#fff';
    }

    // Handle rgb/rgba colors
    const rgbMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
    if (rgbMatch) {
        const r = Number(rgbMatch[1]);
        const g = Number(rgbMatch[2]);
        const b = Number(rgbMatch[3]);
        const brightness = (r * 299 + g * 587 + b * 114) / 1000;
        return brightness > 140 ? '#000' : '#fff';
    }

    // Fallback if color format is unknown
    return '#000';
}

/**
 * TagBadge component - Displays a single tag as a colored badge
 * 
 * Single Responsibility: Visual presentation of a tag
 * 
 * @param {Object} tag - Tag object { tag_id, tag_name, tag_color }
 * @param {Function} onRemove - Optional callback (tag_id) => void
 * @param {string} size - 'sm' | 'md' (default 'sm')
 */
export default function TagBadge({ tag, onRemove, size = 'sm' }) {
    const [isHovered, setIsHovered] = useState(false);
    const { tag_id, tag_name, tag_color } = tag;

    const isRemovable = typeof onRemove === 'function';

    const sizeStyles = {
        sm: { paddingY: 6, paddingX: 8, fontSize: 12 },
        md: { paddingY: 8, paddingX: 12, fontSize: 14 },
    };

    const { paddingY, paddingX, fontSize } = sizeStyles[size] || sizeStyles.sm;

    const badgeStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        backgroundColor: tag_color,
        color: getContrastingTextColor(tag_color),
        padding: `${paddingY}px ${paddingX}px`,
        borderRadius: 4,
        fontSize: `${fontSize}px`,
        lineHeight: 1,
        userSelect: 'none',
        fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    };

    const removeButtonStyle = {
        marginLeft: 6,
        background: 'transparent',
        border: 'none',
        color: 'inherit',
        cursor: 'pointer',
        padding: 0,
        fontSize: `${fontSize - 2}px`,
        lineHeight: 1,
        opacity: isHovered ? 1 : 0,
        pointerEvents: isHovered ? 'auto' : 'none',
        transition: 'opacity 0.15s ease',
        fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    };

    const handleRemove = (event) => {
        event.stopPropagation();
        onRemove(tag_id);
    };

    return (
        <span
            style={badgeStyle}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <span>{tag_name}</span>
            {isRemovable && (
                <button
                    type="button"
                    aria-label={`Remove ${tag_name}`}
                    style={removeButtonStyle}
                    onClick={handleRemove}
                >
                    ×
                </button>
            )}
        </span>
    );
}
